import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { 
  Plus, 
  Search,
  MoreVertical,
  Pencil,
  Trash2,
  UserCircle,
  Mail,
  Phone,
  Calendar,
  Building2
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RelationsPage = () => {
  const [relations, setRelations] = useState([]);
  const [relationTypes, setRelationTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRelation, setEditingRelation] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    entity_kind: 'person',
    relation_type_id: '',
    contact_person: '',
    email: '',
    phone: '',
    website: '',
    address: '',
    postal_code: '',
    city: '',
    chamber_of_commerce_number: '',
    vat_number: '',
    notes: '',
    tags: []
  });

  const fetchRelations = useCallback(async (search = '', relationTypeId = 'all') => {
    try {
      const params = {};
      if (search) params.search = search;
      if (relationTypeId && relationTypeId !== 'all') params.relation_type_id = relationTypeId;
      const [relationsResponse, typesResponse] = await Promise.all([
        axios.get(`${API}/relations`, { params }),
        axios.get(`${API}/relation-types`),
      ]);
      setRelations(relationsResponse.data);
      setRelationTypes(typesResponse.data);
      const defaultType = typesResponse.data.find(type => type.system_key === 'customer' && type.is_active)
        || typesResponse.data.find(type => type.is_active);
      if (defaultType) {
        setFormData(prev => prev.relation_type_id ? prev : { ...prev, relation_type_id: defaultType.id });
      }
    } catch (error) {
      toast.error('Kon relaties niet laden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRelations();
  }, [fetchRelations]);

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    // Debounce search
    const timeoutId = setTimeout(() => {
      fetchRelations(query, typeFilter);
    }, 300);
    return () => clearTimeout(timeoutId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error('Naam is verplicht');
      return;
    }
    if (!formData.relation_type_id) {
      toast.error('Relatietype is verplicht');
      return;
    }

    const payload = Object.fromEntries(
      Object.entries(formData).map(([key, value]) => [key, value === '' ? null : value])
    );

    try {
      if (editingRelation) {
        await axios.put(`${API}/relations/${editingRelation.id}`, payload);
        toast.success('Relatie bijgewerkt');
      } else {
        await axios.post(`${API}/relations`, payload);
        toast.success('Relatie toegevoegd');
      }

      setDialogOpen(false);
      resetForm();
      fetchRelations(searchQuery);
    } catch (error) {
      console.error('Relation error:', error.response?.data || error);
      toast.error(error.response?.data?.detail || 'Actie mislukt');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Weet je zeker dat je deze relatie wilt verwijderen?')) return;
    
    try {
      await axios.delete(`${API}/relations/${id}`);
      toast.success('Relatie verwijderd');
      fetchRelations(searchQuery);
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  const openEditDialog = (relation) => {
    setEditingRelation(relation);
    setFormData({
      name: relation.name,
      entity_kind: relation.entity_kind || 'person',
      relation_type_id: relation.relation_type_id || '',
      contact_person: relation.contact_person || '',
      email: relation.email || '',
      phone: relation.phone || '',
      website: relation.website || '',
      address: relation.address || '',
      postal_code: relation.postal_code || '',
      city: relation.city || '',
      chamber_of_commerce_number: relation.chamber_of_commerce_number || '',
      vat_number: relation.vat_number || '',
      notes: relation.notes || '',
      tags: relation.tags || []
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingRelation(null);
    setFormData({
      name: '',
      entity_kind: 'person',
      relation_type_id: relationTypes.find(type => type.system_key === 'customer')?.id || relationTypes.find(type => type.is_active)?.id || '',
      contact_person: '',
      email: '',
      phone: '',
      website: '',
      address: '',
      postal_code: '',
      city: '',
      chamber_of_commerce_number: '',
      vat_number: '',
      notes: '',
      tags: []
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="h-96 bg-slate-200 rounded-2xl"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="relations-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Relaties</h1>
            <p className="text-slate-500">Beheer externe personen en organisaties</p>
          </div>
          <Button 
            onClick={() => {
              resetForm();
              setDialogOpen(true);
            }}
            className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl"
            data-testid="add-relation-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Relatie toevoegen
          </Button>
        </div>

        {/* Search */}
        <div className="flex flex-col sm:flex-row gap-3 max-w-3xl">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={handleSearch}
              placeholder="Zoek op naam, contactpersoon, plaats, e-mail of telefoon..."
              className="pl-10 h-11 rounded-xl border-slate-200"
              data-testid="relation-search"
            />
          </div>
          <Select
            value={typeFilter}
            onValueChange={(value) => {
              setTypeFilter(value);
              fetchRelations(searchQuery, value);
            }}
          >
            <SelectTrigger className="sm:w-56 h-11 rounded-xl"><SelectValue placeholder="Alle typen" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle relatietypen</SelectItem>
              {relationTypes.map(type => <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Relations Table */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-0">
            {relations.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableHead className="font-semibold">Relatie</TableHead>
                    <TableHead className="font-semibold">Type</TableHead>
                    <TableHead className="font-semibold">Contact</TableHead>
                    <TableHead className="font-semibold text-center">Boekingen</TableHead>
                    <TableHead className="font-semibold">Toegevoegd</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {relations.map((relation) => (
                    <TableRow key={relation.id} className="hover:bg-slate-50">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center text-teal-700 font-medium">
                            {relation.entity_kind === 'organization' ? <Building2 className="w-5 h-5" /> : relation.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-medium text-slate-900">{relation.name}</div>
                            {relation.notes && (
                              <div className="text-xs text-slate-500 truncate max-w-[200px]">
                                {relation.notes}
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="bg-slate-100 text-slate-700">{relation.relation_type_name}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {relation.contact_person && (
                            <div className="text-sm font-medium text-slate-700">{relation.contact_person}</div>
                          )}
                          {relation.email && (
                            <div className="flex items-center gap-2 text-sm text-slate-600">
                              <Mail className="w-3 h-3" />
                              {relation.email}
                            </div>
                          )}
                          {relation.phone && (
                            <div className="flex items-center gap-2 text-sm text-slate-600">
                              <Phone className="w-3 h-3" />
                              {relation.phone}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="inline-flex items-center gap-1 text-sm text-slate-600">
                          <Calendar className="w-3 h-3" />
                          {relation.total_bookings || 0}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {relation.created_at && format(
                          typeof relation.created_at === 'string' 
                            ? parseISO(relation.created_at) 
                            : new Date(relation.created_at),
                          'd MMM yyyy',
                          { locale: nl }
                        )}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(relation)}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Bewerken
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleDelete(relation.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Verwijderen
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="py-12 text-center">
                <UserCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">
                  {searchQuery || typeFilter !== 'all' ? 'Geen resultaten' : 'Geen relaties'}
                </h3>
                <p className="text-slate-500 mb-4">
                  {searchQuery 
                    ? 'Probeer een andere zoekopdracht'
                    : 'Voeg je eerste externe relatie toe om te beginnen'}
                </p>
                {!searchQuery && (
                  <Button 
                    onClick={() => setDialogOpen(true)}
                    className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Eerste relatie toevoegen
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingRelation ? 'Relatie bewerken' : 'Relatie toevoegen'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Soort relatie</Label>
                  <Select value={formData.entity_kind} onValueChange={(value) => setFormData({ ...formData, entity_kind: value })}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="person">Persoon</SelectItem>
                      <SelectItem value="organization">Organisatie</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Relatietype *</Label>
                  <Select value={formData.relation_type_id} onValueChange={(value) => setFormData({ ...formData, relation_type_id: value })}>
                    <SelectTrigger className="rounded-xl"><SelectValue placeholder="Selecteer type" /></SelectTrigger>
                    <SelectContent>
                      {relationTypes.filter(type => type.is_active || type.id === formData.relation_type_id).map(type => (
                        <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">{formData.entity_kind === 'organization' ? 'Organisatienaam' : 'Naam'} *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={formData.entity_kind === 'organization' ? 'Naam organisatie' : 'Volledige naam'}
                  className="rounded-xl"
                  data-testid="relation-name-input"
                />
              </div>

              {formData.entity_kind === 'organization' && (
                <div className="space-y-2">
                  <Label htmlFor="contact_person">Contactpersoon</Label>
                  <Input id="contact_person" value={formData.contact_person} onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })} placeholder="Naam contactpersoon" className="rounded-xl" />
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">E-mail</Label>
                  <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="naam@voorbeeld.nl" className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefoon</Label>
                  <Input id="phone" type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+31 6 12345678" className="rounded-xl" />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="website">Website</Label>
                <Input id="website" type="url" value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} placeholder="https://voorbeeld.nl" className="rounded-xl" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Adres</Label>
                <Input id="address" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder="Straat en huisnummer" className="rounded-xl" />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="postal_code">Postcode</Label>
                  <Input id="postal_code" value={formData.postal_code} onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })} className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city">Plaats</Label>
                  <Input id="city" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} className="rounded-xl" />
                </div>
              </div>

              {formData.entity_kind === 'organization' && (
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="chamber_of_commerce_number">KvK-nummer</Label>
                    <Input id="chamber_of_commerce_number" value={formData.chamber_of_commerce_number} onChange={(e) => setFormData({ ...formData, chamber_of_commerce_number: e.target.value })} className="rounded-xl" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vat_number">Btw-nummer</Label>
                    <Input id="vat_number" value={formData.vat_number} onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })} className="rounded-xl" />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="notes">Notities</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Optionele administratieve notities over deze relatie..."
                  className="rounded-xl resize-none"
                  rows={3}
                />
              </div>

              <DialogFooter className="gap-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setDialogOpen(false)}
                  className="rounded-xl"
                >
                  Annuleren
                </Button>
                <Button 
                  type="submit" 
                  className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl"
                  data-testid="save-relation-btn"
                >
                  {editingRelation ? 'Opslaan' : 'Toevoegen'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default RelationsPage;
